import {
  checkboxClasses,
  createTheme,
  paginationItemClasses,
  typographyClasses
} from '@mui/material'
import { alpha } from '@mui/system'

import {
  brighten,
  hoverAmount,
  hoverTransition
} from 'src/system/ui/theme/hover'

const spacing = Array(60)
  .fill(4)
  .map((base, idx) => `${base * idx}px`)

// The light greys: text, icons and the disabled button's label.
const greys = {
  grey1: '#8d9ba3', // body text: reads 4.5:1 on the page, panels and menu hover
  grey3: '#a6a6a6',
  grey9: '#c7cdd1'
}

// Every dark surface and line, from one blue-grey ramp: surfaces darkest to
// lightest, then line weights. Role keys in `colors` point at these steps; read
// a step directly only when no role fits. Keep new greys on the ramp.
const ramp = {
  sunken: '#1c1e27',
  base: '#1e2029',
  raised: '#232730',
  overlay: '#2c2f3c',
  hover: '#373d4d',
  lineSubtle: '#2b2e39',
  lineDivider: '#2d313f',
  lineStrong: '#353a46',
  lineEmphasis: '#4f5362',
  lineControl: '#6f7a92' // a control's outline: 3:1 on every surface above
}

const palette = {
  ...greys,
  gold: '#c5b358',
  boPeepWhite: '#ffffff',
  csgoT: '#e89230', // amber, kept apart from youDidOkYellow so a T row never reads as a warning
  csgoCt: '#84a9dc',
  youDidGoodGreen: '#62cf7b',
  youDidOkYellow: '#ebc79a',
  faceitOnlyGreen: '#a6cb8b', // between youDidGoodGreen and youDidOkYellow (FACEIT-only status)
  tryAgainRed: '#d88080', // error text: reads 4.5:1 on the page, panels and menu hover
  buttonRed: '#c54343', // the danger button's fill: a white label reads 4.5:1, hovered too
  buttonGreen: '#53ac67', // slightly darker than youDidGoodGreen green
  buttonGreenFill: '#3c7a52', // the brand's Smooth Forest, darkened so a white label reads 4.5:1
  theFanciestPurple: '#9753ad', // fills; text and icons use fancyPurpleText
  fancyPurpleText: '#b788c6', // theFanciestPurple lifted to read 4.5:1 as text
  cuteBabyBlue: '#62cfcf'
}

// The green glow behind an active tab, toggle or sort button, fading out
// towards the far edge. Hovering an inactive tab shows a fainter one.
const activeGlow = alpha(palette.youDidGoodGreen, 0.18)
const hoverGlow = alpha(palette.youDidGoodGreen, 0.08)

const colors = {
  ramp,
  background: ramp.base,
  lifetime: palette.gold,
  text: {
    primary: palette.grey1,
    secondary: palette.boPeepWhite,
    accent: palette.fancyPurpleText
  },
  pendingMatchStatus: {
    completed: palette.grey3,
    created: palette.youDidOkYellow,
    failed: palette.tryAgainRed,
    verified: palette.youDidOkYellow
  },
  button: {
    // The green for text, borders and indicators; fills behind a label read
    // primaryFill.
    primary: palette.buttonGreen,
    primaryFill: palette.buttonGreenFill,
    secondary: ramp.raised,
    danger: palette.buttonRed,
    disabled: {
      background: ramp.overlay,
      text: palette.grey3
    }
  },
  icon: palette.grey9,
  success: palette.youDidGoodGreen,
  faceitOnly: palette.faceitOnlyGreen,
  accent: palette.theFanciestPurple,
  error: palette.tryAgainRed,
  grey: palette.grey3,
  warning: palette.youDidOkYellow,
  // The scoreboard's stat tiers, worst to best.
  statTier: {
    poor: palette.tryAgainRed,
    subpar: palette.youDidOkYellow,
    average: palette.boPeepWhite,
    good: palette.faceitOnlyGreen,
    great: palette.youDidGoodGreen
  },
  link: {
    default: palette.cuteBabyBlue,
    green: palette.buttonGreen
  },
  border: {
    light: ramp.lineSubtle,
    dark: ramp.lineSubtle,
    divider: ramp.lineDivider,
    dialog: ramp.lineStrong,
    input: ramp.lineControl
  },
  panel: {
    light: ramp.raised,
    dark: ramp.sunken
  },
  highlight: ramp.lineDivider,
  ct: palette.csgoCt,
  ctLowOpacity: alpha(palette.csgoCt, 0.4),
  t: palette.csgoT,
  tLowOpacity: alpha(palette.csgoT, 0.4),
  dialog: ramp.raised,
  exclusiveResourceColor: palette.youDidGoodGreen,
  premiumResourceColor: palette.fancyPurpleText,
  promotionBackground: alpha(palette.theFanciestPurple, 0.33),
  promotionText: 'rgba(255, 255, 255, 0.77)',
  expansionTabTitleHighlight: alpha(palette.youDidGoodGreen, 0.15),
  footerBackground: alpha(ramp.base, 0.7),
  tab: {
    hover: `linear-gradient(0, ${hoverGlow} 3.52%, transparent 96.48%)`,
    active: `linear-gradient(0, ${activeGlow} 3.52%, transparent 96.48%)`
  },
  mapCardGrid: `linear-gradient(
    180deg,
    ${ramp.lineSubtle} 0%,
    ${alpha(ramp.base, 0.34)} 100%
  )`,
  activeFade: {
    fromRight: `linear-gradient(270deg, ${activeGlow} 0%, transparent 100%)`,
    fromLeft: `linear-gradient(90deg, ${activeGlow} 0%, transparent 100%)`
  },
  topNavBarLinkBackground: `linear-gradient(0deg, ${activeGlow} 3.52%, transparent 96.48%)`,
  matchHeaderBackground: `
  radial-gradient(
    47.28% 130.44% at 50.67% 51.42%,
    ${alpha(ramp.raised, 0.74)} 0%,
    ${alpha(ramp.base, 0.94)} 54.66%,
    ${ramp.base} 99.54%
  )`,
  starBackground: `
  linear-gradient(
    0deg,
    ${ramp.base} 50%,
    transparent 100%
  )`,
  discordBackground: `
  linear-gradient(
    0deg,
    ${ramp.base} -10%,
    transparent 100%
  )`,
  mapCardBackground: `linear-gradient(
    90deg,
    ${alpha(ramp.base, 0.49)} -40.77%,
    ${ramp.raised} 95.68%
  )`
}

const breakpoints = {
  sm: '600px',
  md: '960px',
  lg: '1280px',
  xl: '1920px'
}

const font = {
  primary: "'Assistant', sans-serif",
  secondary: "'Archive', sans-serif",
  navigation: "'Poppins', sans-serif"
}

const mui = createTheme({
  spacing: 4,
  palette: {
    primary: {
      main: colors.text.primary
    }
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiTypography: {
      variants: [
        {
          props: { color: 'secondary' },
          style: {
            color: colors.text.primary
          }
        }
      ],
      styleOverrides: {
        h1: {
          fontFamily: font.primary
        },
        h2: {
          fontFamily: font.primary
        },
        h3: {
          fontFamily: font.primary
        },
        h4: {
          fontFamily: font.primary
        },
        h5: {
          fontFamily: font.primary
        },
        h6: {
          fontFamily: font.primary
        },
        body1: {
          fontFamily: font.primary,
          color: colors.text.primary
        },
        body2: {
          fontFamily: font.primary,
          color: colors.text.primary
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: colors.background,
          color: colors.text.primary
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          // White on hover: body text reads only just 4.5:1 on the highlight.
          '&:hover': {
            background: colors.highlight,
            color: colors.text.secondary
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paperWidthSm: {
          maxWidth: '420px'
        },
        paper: {
          borderRadius: '6px',
          border: `1px solid ${colors.border.dialog}`
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          background: colors.dialog,
          fontSize: '14px',
          textAlign: 'center',
          '&:first-of-type': {
            paddingTop: '24px'
          }
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          [`& .${typographyClasses.root}`]: {
            fontWeight: 'bold',
            fontSize: '20px',
            textAlign: 'center'
          }
        }
      }
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: colors.text.primary
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          background: colors.dialog,
          justifyContent: 'center',
          padding: spacing[7]
        },
        spacing: {
          '& *': {
            margin: `0 ${spacing[3]}`
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: `${colors.border.input} !important`
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: colors.text.primary
        },
        outlined: {
          background: colors.panel.light,
          color: colors.text.primary,
          fontSize: '14px',
          padding: `${spacing[3]} ${spacing[8]} ${spacing[3]} ${spacing[4]}`
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          border: `1px solid ${colors.border.input}`,
          background: `${colors.panel.light}!important`,
          color: colors.text.primary,
          boxShadow: 'none'
        }
      }
    },
    MuiPagination: {
      styleOverrides: {
        ul: {
          background: colors.button.secondary,
          border: `1px solid ${colors.border.dark}`,
          borderRadius: '4px',
          padding: `${spacing[3]} 0`,
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%'
        }
      }
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
          fontWeight: 'bold',
          fontSize: '13px',
          border: `1px solid ${colors.border.input}`,
          background: colors.panel.dark,
          transition: `background ${hoverTransition}, border-color ${hoverTransition}, color ${hoverTransition}`,
          '&:hover': {
            background: colors.highlight,
            borderColor: colors.button.primary,
            color: colors.text.secondary
          },
          // The ellipsis is a label, not a target - it gets no box, and no
          // hover feedback either.
          [`&.${paginationItemClasses.ellipsis}`]: {
            border: 'none',
            background: 'none',
            '&:hover': {
              background: 'none',
              color: colors.text.primary
            }
          },
          // Greyed rather than faded, so a disabled arrow still reads 4.5:1.
          [`&.${paginationItemClasses.disabled}`]: {
            opacity: 1,
            color: colors.grey
          },
          [`&.${paginationItemClasses.selected}`]: {
            background: colors.button.primaryFill,
            borderColor: colors.button.primaryFill,
            color: colors.text.secondary,
            '&:hover': {
              background: brighten(colors.button.primaryFill, hoverAmount.fill),
              borderColor: brighten(colors.button.primaryFill, hoverAmount.fill)
            }
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.border.input,
          '&:hover': {
            backgroundColor: 'transparent'
          },
          // MUI's disabled default is a near-black tint, invisible here.
          [`&.${checkboxClasses.disabled}`]: {
            color: colors.grey
          }
        },
        colorPrimary: {
          '&:hover': {
            backgroundColor: 'transparent !important'
          }
        }
      }
    }
  }
})

export type ExtendedTheme = {
  breakpoints: typeof breakpoints
  colors: typeof colors
  font: typeof font
  spacing: typeof spacing
}

export const theme = {
  ...mui,
  d: {
    breakpoints,
    colors,
    font,
    spacing
  }
}
